import { RawIngredient } from '../types/rawIngredient';
import { LotNumberOption } from '../types/fillingRecord';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const rawIngredientApiService = {
  /**
   * Fetch all raw ingredients from the backend
   */
  fetchRawIngredients: async (): Promise<RawIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/raw-ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch raw ingredients');
      }

      return data.rawIngredients;
    } catch (error) {
      console.error('Error fetching raw ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Fetch lot numbers for a specific ingredient name
   */
  fetchLotNumbersByName: async (ingredientName: string): Promise<LotNumberOption[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/raw-ingredients/lot-numbers/${encodeURIComponent(ingredientName)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch lot numbers');
      }

      return data.lotNumbers;
    } catch (error) {
      console.error('Error fetching lot numbers from API:', error);
      throw error;
    }
  },

  /**
   * Fetch unique ingredient names from raw ingredients
   */
  fetchIngredientNames: async (): Promise<string[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/raw-ingredients/names`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient names');
      }

      return data.ingredientNames;
    } catch (error) {
      console.error('Error fetching ingredient names from API:', error);
      throw error;
    }
  },

  /**
   * Create a new raw ingredient
   */
  createRawIngredient: async (rawIngredient: RawIngredient): Promise<RawIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/raw-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rawIngredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create raw ingredient');
      }

      return data.rawIngredient;
    } catch (error) {
      console.error('Error creating raw ingredient:', error);
      throw error;
    }
  },

  /**
   * Update an existing raw ingredient
   */
  updateRawIngredient: async (rawIngredient: RawIngredient): Promise<RawIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/raw-ingredients/${rawIngredient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rawIngredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update raw ingredient');
      }

      return data.rawIngredient;
    } catch (error) {
      console.error('Error updating raw ingredient:', error);
      throw error;
    }
  },

  /**
   * Delete a raw ingredient by ID
   */
  deleteRawIngredient: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/raw-ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete raw ingredient');
      }
    } catch (error) {
      console.error('Error deleting raw ingredient:', error);
      throw error;
    }
  },

  /**
   * Import multiple raw ingredients from localStorage (batch migration)
   */
  importRawIngredientsBatch: async (ingredients: RawIngredient[]): Promise<{ success: boolean; count: number }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/raw-ingredients/import-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ingredients })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to import raw ingredients');
      }

      return { success: true, count: data.count };
    } catch (error) {
      console.error('Error importing raw ingredients:', error);
      throw error;
    }
  }
};
